import { Dialog, DialogTrigger, DialogContent, DialogClose } from '@/components/atoms/Dialog';
import { Button } from '@/components/atoms/PcButton';
import { useState } from 'react';
import { Check } from 'lucide-react';
import { cn } from '@/utils/tailwind';
import { useToast } from '@/hooks/useToast';
import { cplatApiV2 } from '@/api';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '@/redux/store';
import { ROUTES } from '@/routes';

export async function requestUnsubscribe({
  cplatToken,
  ReasonCode,
  Memo,
}: {
  cplatToken: string;
  ReasonCode: string;
  Memo: string;
}) {
  return cplatApiV2.post('/user/unsubscribe', {
    cplatToken,
    ReasonCode,
    Memo,
  });
}
const GetOutClient = () => {
  const [selected, setSelected] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [marketDialogOpen, setMarketDialogOpen] = useState(false);
  const { toast } = useToast();
  const [memo, setMemo] = useState('');
  const navigate = useNavigate();
  const user = useAppSelector((state) => state.user);
  // 탈퇴 처리 함수
  const handleUnsubscribe = async () => {
    if (!selected) return;
    setIsLoading(true);
    try {
      const res = await requestUnsubscribe({
        cplatToken: user.CplatToken, // cplatToken 추가
        ReasonCode: selected,
        Memo: memo,
      });
      // code 분기 처리
      const { code } = res.data || {};
      if (parseInt(code) === 200) {
        localStorage.clear();
        toast({
          icon: '✅',
          title: '회원 탈퇴가 완료되었습니다.',
          variant: 'dark',
        });
        window.location.href = 'https://www.sellerbox.io/services';
      } else if (parseInt(code) === 720) {
        setMarketDialogOpen(true); // 마켓 해제 안내 다이얼로그 오픈
      } else {
        toast({
          icon: '🚨',
          title: '회원 탈퇴 중 오류가 발생했습니다. 다시 시도해 주세요.',
          variant: 'error',
        });
      }
    } catch (e) {
      toast({
        icon: '🚨',
        title: '회원 탈퇴 중 오류가 발생했습니다. 다시 시도해 주세요.',
        variant: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // 마켓 해제 안내 다이얼로그에서 확인 버튼 클릭 시
  const handleGoToMarket = () => {
    setMarketDialogOpen(false);
    navigate(ROUTES.market);
  };

  return (
    <>
      {/* 회원탈퇴 메인 다이얼로그 */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogTrigger asChild>
          <Button variant="ghost" className="text-gr-700 shadow-none Body5M14">
            회원 탈퇴
          </Button>
        </DialogTrigger>
        <DialogContent noCloseBtn className="w-full max-w-[320px]">
          <section className="flex flex-col gap-[4px]">
            <h2 className="text-gr-900 Title3S18">정말 탈퇴하시는건가요? 아쉬워요🙁</h2>
            <h5 className="text-gr-700 Body5M14">연동된 마켓을 모두 해지해야 탈퇴가 가능해요</h5>
          </section>
          <DisappointingReasonList selected={selected} onSelect={setSelected} />
          {selected === '0005' && (
            <textarea
              className="mt-2 w-full resize-none rounded border p-2 text-sm focus:outline-none focus:ring-2  focus:ring-point-500"
              placeholder="탈퇴 사유를 알려주세요"
              value={memo}
              maxLength={150}
              onChange={(e) => setMemo(e.target.value)}
            />
          )}
          <div className="mt-[20px] flex w-full gap-3">
            <Button
              variant="muted"
              className="h-[44px] flex-1 rounded-[16px] Body1S16 disabled:bg-gr-200"
              disabled={!selected || isLoading}
              onClick={handleUnsubscribe}
              loading={isLoading}
            >
              탈퇴하기
            </Button>
            <DialogClose asChild>
              <Button
                className="h-[44px] flex-1 rounded-[16px] Body1S16 disabled:bg-gr-200"
                disabled={isLoading}
              >
                취소
              </Button>
            </DialogClose>
          </div>
        </DialogContent>
      </Dialog>

      {/* 720 에러: 마켓 해제 안내 다이얼로그 */}
      <Dialog open={marketDialogOpen} onOpenChange={setMarketDialogOpen}>
        <DialogContent noCloseBtn className="w-full max-w-[320px]">
          <section className="flex flex-col gap-[8px]">
            <h2 className="text-gr-900 Title3S18">연동된 마켓을 모두 해제해 주세요</h2>
            <p className="whitespace-pre-line text-gr-700 Body5M14">
              연동된 마켓을 모두 해제하신 후에{'\n'}탈퇴를 진행할 수 있어요.
            </p>
          </section>
          <div className="mt-[20px] flex w-full gap-3">
            <Button className="h-[44px] flex-1 rounded-[16px] Body1S16" onClick={handleGoToMarket}>
              마켓연동 해제하기
            </Button>
            <DialogClose asChild>
              <Button className="h-[44px] flex-1 rounded-[16px] Body1S16" variant="muted">
                닫기
              </Button>
            </DialogClose>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default GetOutClient;
const DisappointingReasonList = ({ selected, onSelect }: Props) => {
  return (
    <div className="flex flex-col items-start gap-[12px]">
      {DISAPPOINTING_NEWS_OPTIONS.map(({ code, text }) => {
        const isSelected = selected === code;
        return (
          <button
            key={code}
            onClick={() => onSelect(isSelected ? '' : code)}
            className={cn(
              'flex h-[24px] w-full items-center justify-between text-[15px] font-medium leading-[22px] transition-colors duration-500',
              isSelected ? 'text-point-500' : 'text-gr-700',
            )}
          >
            <div>{text}</div>
            <Check
              className="h-[24px] w-[24px] text-point-500 transition-all duration-300"
              style={{ opacity: isSelected ? 1 : 0 }}
            />
          </button>
        );
      })}
    </div>
  );
};

export type FeedbackOption = {
  code: string;
  text: string;
};

export const DISAPPOINTING_NEWS_OPTIONS: FeedbackOption[] = [
  { code: '0001', text: '서비스 컨텐츠가 부족해서' },
  { code: '0002', text: '제공 정보가 부정확해서' },
  { code: '0003', text: '연동 가능한 마켓이 없어서' },
  { code: '0004', text: '서비스 이용이 어렵고 불편해서' },
  { code: '0005', text: '기타' },
];
